// The Dispatch / Production / Warehouse team's dashboard. They used to fall
// through to the Sales Admin dashboard (any admin outside IT/Marketing did),
// which showed an empty sales pipeline and asked for /api/deals — refused with
// a 403, since Dispatch has no Sales access. Everything here comes from what
// Dispatch already works with: the dispatch queue and the local transport
// register.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Boxes, CheckCircle2, ChevronRight, Clock, Coins, FileStack, PackageX, Truck, Users } from 'lucide-react';
import { WelcomeBanner, type BannerTone } from '../infrastructure/widgets/WelcomeBanner';
import { api } from '@/shared/api/axios';
import { useUser } from '@/shared/hooks/useAuth';
import { canSetTransportAmount } from '@/shared/utils/dispatchAccess';
import { formatINR } from '@/shared/utils/currency';
import { ROUTES } from '@/routes/paths';

interface QueueRow {
  _id: string;
  orderNumber: string;
  customerName: string;
  shippingDestination: string;
  grandTotal: number;
  transportName: string;
  stage: string;
  requiredDispatchDate: string | null;
  isOverdue: boolean;
}

const todayKey = () => new Date().toLocaleDateString('en-CA');

export function DispatchDashboard() {
  const user = useUser();
  const { data: queue } = useQuery({
    queryKey: ['dispatch', 'queue', 'dispatch-dashboard'],
    queryFn: async () => (await api.get<{ data: QueueRow[]; kpis: { awaitingDispatch: number; draftChallans: number; dispatchedToday: number; stockShort: number } }>('/api/sales-ops/orders/dispatch-queue')).data,
    refetchInterval: 60_000,
  });
  const today = todayKey();
  // Transport costs: Superadmin / Admin / Department Administrator only.
  const seesAmounts = canSetTransportAmount(user);
  const { data: local } = useQuery({
    enabled: seesAmounts,
    queryKey: ['dispatch', 'local-transport-log', today, today],
    queryFn: async () => (await api.get<{ rows: { localTransport: { amount?: number } }[] }>('/api/sales-ops/orders/local-transport-log', { params: { from: today, to: today } })).data.rows,
  });

  const rows = queue?.data ?? [];
  const pending = useMemo(() => rows.filter((r) => r.stage !== 'dispatched' && r.stage !== 'delivered'), [rows]);
  const overdue = pending.filter((r) => r.isOverdue);
  const next = [...pending].sort((a, b) => (a.requiredDispatchDate || '9').localeCompare(b.requiredDispatchDate || '9')).slice(0, 8);
  const localToday = (local ?? []).reduce((n, r) => n + (Number(r.localTransport?.amount) || 0), 0);

  const kpis: { label: string; value: string; sub: string; icon: typeof Truck; tone: BannerTone }[] = [
    { label: 'Awaiting dispatch', value: String(queue?.kpis.awaitingDispatch ?? pending.length), sub: formatINR(pending.reduce((n, r) => n + (Number(r.grandTotal) || 0), 0)), icon: Truck, tone: 'primary' },
    { label: 'Overdue', value: String(overdue.length), sub: overdue.length ? 'past their dispatch date' : 'none', icon: AlertTriangle, tone: overdue.length ? 'danger' : 'success' },
    { label: 'Dispatched today', value: String(queue?.kpis.dispatchedToday ?? 0), sub: 'left the warehouse', icon: CheckCircle2, tone: 'success' },
    { label: 'Stock short', value: String(queue?.kpis.stockShort ?? 0), sub: 'awaiting BDM response', icon: PackageX, tone: 'warning' },
    ...(seesAmounts ? [{ label: 'Local transport today', value: formatINR(localToday), sub: `${(local ?? []).length} trip(s)`, icon: Coins, tone: 'info' as BannerTone }] : []),
  ];
  const destinations: [string, string, string, typeof Truck][] = [
    ['Pending Dispatch', 'Orders handed over by Sales', ROUTES.salesDispatchTracking, Truck],
    ['Dispatch & Challans', 'Challans, LR and proof of delivery', ROUTES.dispatchOrders, FileStack],
    ['Fleet & Drivers', 'Vehicles and who is driving', ROUTES.fleetManagement, Users],
    ['Inventory', 'Stock on hand before you load', ROUTES.inventory, Boxes],
  ];

  return (
    <div className="mx-auto max-w-[1400px] space-y-4 pb-14 text-on-surface">
      <WelcomeBanner
        subtitle={overdue.length
          ? <><span className="font-mono font-semibold tabular-nums text-danger-text">{overdue.length}</span> order{overdue.length === 1 ? ' is' : 's are'} past the dispatch date — they go first.</>
          : 'Every handed-over order is on time.'}
        stats={kpis}
      />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <section className="overflow-hidden rounded-card border border-border bg-card shadow-surface-1 lg:col-span-2">
          <div className="flex items-center justify-between border-b border-border bg-surface-container-low/60 px-5 py-3">
            <h3 className="flex items-center gap-2.5 text-body-md font-semibold text-on-surface">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-container text-primary-dim"><Truck className="h-4 w-4" /></span>
              Next to go out
            </h3>
            <Link to={ROUTES.salesDispatchTracking} className="flex items-center gap-0.5 text-body-sm font-semibold text-primary hover:underline">Pending Dispatch <ChevronRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="divide-y divide-slate-50">
            {next.length === 0 ? <div className="py-8 text-center text-body-sm text-on-surface-subtle">Nothing waiting — all clear.</div> : next.map((r) => (
              <Link key={r._id} to={`/dispatch/orders/${r._id}`} className="flex items-center justify-between px-5 py-2.5 text-body-sm hover:bg-surface-variant">
                <div className="min-w-0">
                  <div className="font-semibold text-on-surface">{r.orderNumber} · {r.customerName}</div>
                  <div className="text-data-xs text-on-surface-variant">{r.shippingDestination || '—'} · {r.transportName || '—'}</div>
                </div>
                <div className="ml-3 shrink-0 text-right">
                  <div className={`flex items-center justify-end gap-1 font-semibold ${r.isOverdue ? 'text-danger-text' : 'text-on-surface-variant'}`}>
                    <Clock className="h-3 w-3" />{r.requiredDispatchDate ? new Date(r.requiredDispatchDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : 'no date'}{r.isOverdue ? ' · overdue' : ''}
                  </div>
                  <div className="text-data-xs tabular-nums text-on-surface-variant">{formatINR(r.grandTotal)}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
        <section className="overflow-hidden rounded-card border border-border bg-card shadow-surface-1">
          <div className="border-b border-border bg-surface-container-low/60 px-5 py-3">
            <h3 className="text-body-md font-semibold text-on-surface">Go to</h3>
          </div>
          <div className="space-y-2 p-4">
            {destinations.map(([label, hint, to, Icon]) => (
              <Link key={label} to={to} className="group flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 transition hover:border-primary/30 hover:bg-primary-container/40">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-container text-primary-dim"><Icon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-body-sm font-semibold text-on-surface">{label}</span>
                  <span className="block truncate text-data-xs text-on-surface-variant">{hint}</span>
                </span>
                <ChevronRight className="h-4 w-4 text-on-surface-subtle transition group-hover:translate-x-0.5 group-hover:text-primary" />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
